import { DEFAULT_SEGMENT } from '#client/build-env';

/** @typedef {import('@zenbusiness/application-commons-business-entity').Entitlements} Entitlements */
/** @typedef {import('../config/FlowConfigBase').ScreensToSkipByService} ScreensToSkipByService */

/**
 * Get the index of a page in the flow.
 *
 * @param {object} params - Parameters for getting the page index.
 * @param {Array<object>} params.pages - The flow pages.
 * @param {string} params.page - The page identifier for which to get the index.
 * @returns {number} - The index of the page, or -1 if not found.
 */
export function getPageIndex({ pages, page }) {
  return pages?.findIndex((p) => p.path === page);
}

/**
 * Get the next page based on the current page.
 *
 * @param {object} params - Parameters for getting the next page.
 * @param {Array<object>} params.pages - The flow pages.
 * @param {string} params.currentPage - The current page identifier.
 * @returns {import("../config/flowPageConfig").Page | null} - The next page object, or null if not found.
 */
export function getNextPage({ pages, currentPage }) {
  const currentPageIndex = getPageIndex({ pages, page: currentPage });

  if (currentPageIndex === -1) {
    return null;
  }

  return pages[currentPageIndex + 1] || null;
}

/**
 * Get the previous page based on the current page.
 *
 * @param {object} params - Parameters for getting the previous page.
 * @param {Array<object>} params.pages - The flow pages.
 * @param {string} params.currentPage - The current page identifier.
 * @returns {import("../config/flowPageConfig").Page | null} - The previous page object, or null if not found.
 */
export function getPrevPage({ pages, currentPage }) {
  const currentPageIndex = getPageIndex({ pages, page: currentPage });

  if (currentPageIndex === -1) {
    return null;
  }

  return pages[currentPageIndex - 1] || null;
}

/**
 * Get the full path of a given page.
 *
 * @param {object} params - Parameters for getting the page path.
 * @param {string} params.flowId - The flow identifier.
 * @param {string} params.page - The page identifier for which to get the path.
 * @returns {string|null} - The full path of the page.
 */
export function getPagePath({ flowId, page }) {
  if (!flowId || !page) {
    return null;
  }
  return `${DEFAULT_SEGMENT}/${flowId}/${page}`;
}

/**
 * Get the first page in the specified flow.
 *
 * @param {Array<object>} pages - The flow pages.
 * @returns {import("../config/flowPageConfig").Page|null} - The path of the first page.
 */
export function getFirstPage(pages) {
  return pages[0]?.path ?? null;
}

/**
 * Check if a page exists in the current flow.
 *
 * @param {Array<object>} pages - The flow pages.
 * @param {string} page - The page identifier to check.
 * @returns {boolean} - True if the page exists, false otherwise.
 */
export function isPageValid(pages, page) {
  return pages.some((p) => p.path === page);
}

/**
 * Checks if a page should be skipped based on its skips functions.
 *
 * @param {object} params - Parameters for checking if the page should be skipped.
 * @param {boolean} params.authenticated - Indicates if the user is authenticated.
 * @param {object} params.businessEntity - The business entity.
 * @param {object} params.businessInfo - The business information.
 * @param {object} params.cartContents - The current cart contents.
 * @param {Entitlements} params.entitlements - The business entitlements.
 * @param {object} params.page - The page object containing the skip functions.
 * @param {Array<ScreensToSkipByService>} params.screensToSkip - The screens to skip.
 * @returns {boolean} - True if the page should be skipped, false otherwise.
 */
export function shouldSkipPage({
  authenticated,
  businessEntity,
  businessInfo,
  cartContents,
  entitlements,
  page,
  screensToSkip
}) {
  return page.skips.some((skipFn) =>
    skipFn({
      authenticated,
      businessEntity,
      businessInfo,
      cartContents,
      entitlements,
      page,
      screensToSkip
    })
  );
}

/**
 * Finds the next page that should not be skipped.
 *
 * @param {object} params - Parameters for finding the next page.
 * @param {boolean} params.authenticated - Indicates if the user is authenticated.
 * @param {object} params.businessEntity - The business entity.
 * @param {object} params.businessInfo - The business information.
 * @param {object} params.cartContents - The current cart contents.
 * @param {string} params.currentPage - The current page identifier.
 * @param {Entitlements} params.entitlements - The business entitlements.
 * @param {Array<object>} params.pages - The flow pages.
 * @param {Array<ScreensToSkipByService>} params.screensToSkip - The screens to skip.
 * @returns {import("../config/flowPageConfig").Page|null} - The next page that should not be skipped, or null if not found.
 */
export function findNextPage({
  authenticated,
  businessEntity,
  businessInfo,
  cartContents,
  currentPage,
  entitlements,
  pages,
  screensToSkip
}) {
  let next = getNextPage({ pages, currentPage });

  while (
    next &&
    shouldSkipPage({
      authenticated,
      businessEntity,
      businessInfo,
      cartContents,
      entitlements,
      page: next,
      screensToSkip
    })
  ) {
    next = getNextPage({ pages, currentPage: next.path });
  }

  return next;
}

/**
 * Finds the previous page that should not be skipped.
 *
 * @param {object} params - Parameters for finding the previous page.
 * @param {boolean} params.authenticated - Indicates if the user is authenticated.
 * @param {object} params.businessEntity - The business entity.
 * @param {object} params.businessInfo - The business information.
 * @param {object} params.cartContents - The current cart contents.
 * @param {string} params.currentPage - The current page identifier.
 * @param {Entitlements} params.entitlements - The business entitlements.
 * @param {Array<object>} params.pages - The flow pages.
 * @param {Array<ScreensToSkipByService>} params.screensToSkip - The screens to skip.
 * @returns {import("../config/flowPageConfig").Page|null} - The previous page that should not be skipped, or null if not found.
 */
export function findPrevPage({
  authenticated,
  businessEntity,
  businessInfo,
  cartContents,
  currentPage,
  entitlements,
  pages,
  screensToSkip
}) {
  let prev = getPrevPage({ pages, currentPage });

  while (
    prev &&
    shouldSkipPage({
      authenticated,
      businessEntity,
      businessInfo,
      cartContents,
      entitlements,
      page: prev,
      screensToSkip
    })
  ) {
    prev = getPrevPage({ pages, currentPage: prev.path });
  }

  return prev;
}
